<h2 mat-dialog-title class="lmc-dialog-title">Run Sanity Check</h2>

<mat-dialog-content class="sanity-check-content">

    <p class="lmc-dialog-description">
        Two checks over the loans with a scheduled payment in the selected due-date range —
        active loans holding an active pre-authorized debit setup. Both run on one Execute;
        each tab explains what it looked for.
    </p>

    <!-- Dates and actions share one row, mirroring the filter row on the list page. -->
    <form [formGroup]="form">
        <formly-form [form]="form" [fields]="fields" [model]="model"></formly-form>

        <div class="date-range-actions">
            <button mat-stroked-button type="button" (click)="onCancel()">Cancel</button>
            <button mat-raised-button color="primary" type="button"
                    [disabled]="!isRangeValid || isRunning"
                    (click)="onExecute()">
                <mat-spinner *ngIf="isRunning" diameter="18" class="btn-spinner"></mat-spinner>
                <mat-icon *ngIf="!isRunning">play_arrow</mat-icon>
                Execute
            </button>
        </div>
    </form>

    <!-- Error notice -->
    <div class="result-notice error-notice" *ngIf="errorMessage">
        <mat-icon>error_outline</mat-icon>
        <span>{{ errorMessage }}</span>
    </div>

    <!--
        One tab per check. Both are filled by the same run, so they are tabs rather than two
        stacked sections: the counts are visible at a glance on the labels, and neither
        result has to be scrolled past to reach the other. They are on screen before the
        first run too — the dialog's height is fixed, so an empty frame would be a blank
        panel; a placeholder says what will fill it instead.
    -->
    <mat-tab-group class="sanity-tabs" animationDuration="0ms">

        <!-- ── Check 1: scheduled payment vs the loan's expected installment ── -->
        <mat-tab>
            <ng-template mat-tab-label>
                <span class="tab-label" [class.has-findings]="hasResults && invalidCount > 0">
                    <span>Invalid Payments</span>
                    <span class="tab-count" *ngIf="hasResults">{{ invalidCount }}</span>
                </span>
            </ng-template>

            <!-- The label's count says how many; only the fact that some were left out
                 needs stating, and only when it happens. -->
            <p class="truncation-hint" *ngIf="truncated">
                Only the first {{ invalidCount }} are shown — narrow the date range to see the rest.
            </p>

            <!-- fact_check is the icon the Run Sanity Check button carries on the list page:
                 the check itself, idle. An empty state depicts the state, not an action —
                 a play icon here would read as a control, and this one is not clickable. -->
            <div class="empty-state" *ngIf="!hasResults">
                <mat-icon>fact_check</mat-icon>
                <p>No results yet</p>
                <span>Pick a due-date range and run the check.</span>
            </div>

            <div class="empty-state" *ngIf="hasResults && invalidCount === 0">
                <mat-icon>check_circle_outline</mat-icon>
                <p>No invalid payments</p>
                <span>Every scheduled payment in this range matches its expected amount.</span>
            </div>

            <!-- Invalid payments table -->
            <div class="table-section" *ngIf="invalidCount > 0">
                <div class="table-container">
                    <table mat-table [dataSource]="dataSource" class="flat-table" multiTemplateDataRows>

                        <!-- Expand toggle -->
                        <ng-container matColumnDef="expand">
                            <th mat-header-cell *matHeaderCellDef class="expand-cell"></th>
                            <td mat-cell *matCellDef="let row" class="expand-cell">
                                <button mat-icon-button type="button"
                                        [attr.aria-label]="(isExpanded(row) ? 'Collapse' : 'Expand') + ' payment breakdown'"
                                        [attr.aria-expanded]="isExpanded(row)"
                                        (click)="$event.stopPropagation(); toggleExpanded(row)">
                                    <mat-icon>{{ isExpanded(row) ? 'expand_less' : 'expand_more' }}</mat-icon>
                                </button>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="loanNumber">
                            <th mat-header-cell *matHeaderCellDef>Loan Number</th>
                            <td mat-cell *matCellDef="let row">{{ row.loanNumber }}</td>
                        </ng-container>

                        <ng-container matColumnDef="customerName">
                            <th mat-header-cell *matHeaderCellDef>Customer</th>
                            <td mat-cell *matCellDef="let row">{{ row.customerName || '-' }}</td>
                        </ng-container>

                        <ng-container matColumnDef="dueDate">
                            <th mat-header-cell *matHeaderCellDef>Due Date</th>
                            <td mat-cell *matCellDef="let row" class="date-cell">{{ row.dueDate }}</td>
                        </ng-container>

                        <ng-container matColumnDef="maturityDate">
                            <th mat-header-cell *matHeaderCellDef>Maturity Date</th>
                            <td mat-cell *matCellDef="let row" class="date-cell"
                                matTooltip="loanacct.curr_maturity_date — the loan's maturity as it stands today">
                                {{ row.maturityDate || '-' }}
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="scheduledAmount">
                            <th mat-header-cell *matHeaderCellDef>Scheduled Amount</th>
                            <td mat-cell *matCellDef="let row" class="amount-cell"
                                matTooltip="payment_amount on loanacct_payments_due">
                                {{ FormatUtil.formatCurrency(row.scheduledAmount) }}
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="expectedAmount">
                            <th mat-header-cell *matHeaderCellDef>Expected Amount</th>
                            <td mat-cell *matCellDef="let row" class="amount-cell"
                                [matTooltip]="expectedBreakdown(row)">
                                {{ FormatUtil.formatCurrency(row.expectedAmount) }}
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="difference">
                            <th mat-header-cell *matHeaderCellDef>Difference</th>
                            <td mat-cell *matCellDef="let row" class="amount-cell difference-cell"
                                [class.is-negative]="difference(row) < 0">
                                {{ FormatUtil.formatCurrency(difference(row)) }}
                            </td>
                        </ng-container>

                        <!-- Expanded breakdown: the payment's non-ZZ rows, sharing its reference -->
                        <ng-container matColumnDef="breakdown">
                            <td mat-cell *matCellDef="let row" class="breakdown-cell"
                                [attr.colspan]="displayedColumns.length">
                                <div class="breakdown" *ngIf="isExpanded(row)">
                                    <div class="breakdown-header">
                                        <span class="breakdown-title">Payment breakdown</span>
                                        <span class="breakdown-reference">
                                            payment_reference_no {{ row.paymentReferenceNo }}
                                        </span>
                                    </div>

                                    <table class="breakdown-table" *ngIf="row.components.length > 0">
                                        <thead>
                                            <tr>
                                                <th>Pmt #</th>
                                                <th>Type</th>
                                                <th>Description</th>
                                                <th class="num">Amount</th>
                                                <th class="num">Paid</th>
                                                <th class="num">Remaining</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr *ngFor="let c of row.components">
                                                <td>{{ c.paymentNumber }}</td>
                                                <td><span class="type-badge">{{ c.paymentType }}</span></td>
                                                <td>{{ c.paymentDescription }}</td>
                                                <td class="num">{{ FormatUtil.formatCurrency(c.amount) }}</td>
                                                <td class="num">{{ FormatUtil.formatCurrency(c.paid) }}</td>
                                                <td class="num">{{ FormatUtil.formatCurrency(c.remaining) }}</td>
                                            </tr>
                                        </tbody>
                                        <tfoot>
                                            <!-- The components normally sum to exactly this; the rare case where
                                                 they don't is called out by the warning below, which quotes both. -->
                                            <tr>
                                                <td colspan="3">TOT PAYMENT</td>
                                                <td class="num">{{ FormatUtil.formatCurrency(row.scheduledAmount) }}</td>
                                                <td colspan="2"></td>
                                            </tr>
                                        </tfoot>
                                    </table>

                                    <p class="breakdown-empty" *ngIf="row.components.length === 0">
                                        This payment has no component rows — only its TOT PAYMENT total.
                                    </p>

                                    <div class="breakdown-warning" *ngIf="hasComponentMismatch(row)">
                                        <mat-icon>warning_amber</mat-icon>
                                        <span>
                                            The components do not add up to the charged amount
                                            ({{ FormatUtil.formatCurrency(componentsTotal(row)) }} vs
                                            {{ FormatUtil.formatCurrency(row.scheduledAmount) }}) — the NLS total row
                                            disagrees with its own breakdown.
                                        </span>
                                    </div>
                                </div>
                            </td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                        <tr mat-row *matRowDef="let row; columns: displayedColumns"
                            class="summary-row"
                            [class.is-expanded]="isExpanded(row)"
                            (click)="toggleExpanded(row)"
                            (keydown.enter)="toggleExpanded(row)"
                            tabindex="0"></tr>
                        <tr mat-row *matRowDef="let row; columns: ['breakdown']"
                            class="breakdown-row"
                            [class.is-collapsed]="!isExpanded(row)"></tr>

                    </table>
                </div>

                <mat-paginator #invalidPaginator
                    [pageSize]="pageSize"
                    [pageSizeOptions]="[10, 20, 50]"
                    showFirstLastButtons
                    (page)="onPageChange($event)">
                </mat-paginator>
            </div>
        </mat-tab>

        <!-- ── Check 2: customers behind those loans holding more than one ── -->
        <mat-tab>
            <ng-template mat-tab-label>
                <span class="tab-label" [class.has-findings]="hasResults && multiLoanCount > 0">
                    <span>Customers with Multiple Loans</span>
                    <span class="tab-count" *ngIf="hasResults">{{ multiLoanCount }}</span>
                </span>
            </ng-template>

            <p class="truncation-hint" *ngIf="multiLoanTruncated">
                Only the first {{ multiLoanCount }} are shown — narrow the date range to see the rest.
            </p>

            <div class="empty-state" *ngIf="!hasResults">
                <mat-icon>fact_check</mat-icon>
                <p>No results yet</p>
                <span>Pick a due-date range and run the check.</span>
            </div>

            <div class="empty-state" *ngIf="hasResults && multiLoanCount === 0">
                <mat-icon>check_circle_outline</mat-icon>
                <p>No customer with multiple loans</p>
                <span>Every customer in this range holds a single active loan.</span>
            </div>

            <div class="table-section" *ngIf="multiLoanCount > 0">
                <div class="table-container">
                    <table mat-table [dataSource]="multiLoanDataSource" class="flat-table" multiTemplateDataRows>

                        <ng-container matColumnDef="expandCustomer">
                            <th mat-header-cell *matHeaderCellDef class="expand-cell"></th>
                            <td mat-cell *matCellDef="let row" class="expand-cell">
                                <button mat-icon-button type="button"
                                        [attr.aria-label]="(isCustomerExpanded(row) ? 'Collapse' : 'Expand') + ' loan list'"
                                        [attr.aria-expanded]="isCustomerExpanded(row)"
                                        (click)="$event.stopPropagation(); toggleCustomerExpanded(row)">
                                    <mat-icon>{{ isCustomerExpanded(row) ? 'expand_less' : 'expand_more' }}</mat-icon>
                                </button>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="customerName">
                            <th mat-header-cell *matHeaderCellDef>Customer</th>
                            <td mat-cell *matCellDef="let row">{{ row.customerName || '-' }}</td>
                        </ng-container>

                        <ng-container matColumnDef="cifnos">
                            <th mat-header-cell *matHeaderCellDef>CIF No</th>
                            <td mat-cell *matCellDef="let row"
                                [class.is-duplicate-cif]="row.cifnos.length > 1"
                                [matTooltip]="row.cifnos.length > 1
                                    ? 'NLS holds ' + row.cifnos.length + ' CIF records for this person — the loans were tied together by name and bank account'
                                    : 'loanacct.cifno — the NLS customer record'">
                                {{ cifnos(row) }}
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="linkedBy">
                            <th mat-header-cell *matHeaderCellDef>Linked By</th>
                            <td mat-cell *matCellDef="let row"
                                matTooltip="How these loans were tied to one customer: a shared CIF record, or the same name on the same bank account">
                                {{ row.linkedBy }}
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="loanCount">
                            <th mat-header-cell *matHeaderCellDef>Loans</th>
                            <td mat-cell *matCellDef="let row" class="amount-cell">{{ row.loanCount }}</td>
                        </ng-container>

                        <ng-container matColumnDef="loanNumbers">
                            <th mat-header-cell *matHeaderCellDef>Loan Numbers</th>
                            <td mat-cell *matCellDef="let row" class="id-list-cell"
                                [matTooltip]="loanNumbers(row)">
                                {{ loanNumbers(row) }}
                            </td>
                        </ng-container>

                        <!-- Expanded: every active loan the customer holds -->
                        <ng-container matColumnDef="customerLoans">
                            <td mat-cell *matCellDef="let row" class="breakdown-cell"
                                [attr.colspan]="customerColumns.length">
                                <div class="breakdown" *ngIf="isCustomerExpanded(row)">
                                    <div class="breakdown-header">
                                        <span class="breakdown-title">Active loans</span>
                                    </div>

                                    <!-- Due date and amount are the loan's next scheduled NLS payment;
                                         a sibling loan's normally falls outside the checked range, which
                                         is what says when that loan will draw. -->
                                    <table class="breakdown-table">
                                        <thead>
                                            <tr>
                                                <th>Loan Number</th>
                                                <th>Acctrefno</th>
                                                <th>CIF No</th>
                                                <th>Due Date</th>
                                                <th class="num">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr *ngFor="let loan of row.loans">
                                                <td>{{ loan.loanNumber }}</td>
                                                <td>{{ loan.acctrefno }}</td>
                                                <td>{{ loan.cifno }}</td>
                                                <td class="date-cell">{{ loan.dueDate || '-' }}</td>
                                                <td class="num">
                                                    {{ loan.paymentAmount === null ? '-' : FormatUtil.formatCurrency(loan.paymentAmount) }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="customerColumns"></tr>
                        <tr mat-row *matRowDef="let row; columns: customerColumns"
                            class="summary-row"
                            [class.is-expanded]="isCustomerExpanded(row)"
                            (click)="toggleCustomerExpanded(row)"
                            (keydown.enter)="toggleCustomerExpanded(row)"
                            tabindex="0"></tr>
                        <tr mat-row *matRowDef="let row; columns: ['customerLoans']"
                            class="breakdown-row"
                            [class.is-collapsed]="!isCustomerExpanded(row)"></tr>

                    </table>
                </div>

                <mat-paginator #multiLoanPaginator
                    [pageSize]="multiLoanPageSize"
                    [pageSizeOptions]="[10, 20, 50]"
                    showFirstLastButtons
                    (page)="onMultiLoanPageChange($event)">
                </mat-paginator>
            </div>
        </mat-tab>

    </mat-tab-group>

</mat-dialog-content>